import { useEffect } from 'react';

import { useOrg } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import type { HyperionFeatureAccess } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import { usePropertyDetailsError } from 'src/modules/propertyDetails/hooks/usePropertyDetailsError';
import {
  FeatureAccessIdentifier,
  useProfileQuery,
} from 'src/shared/graphql/generated';

enum ActiveCommerceProduct {
  ESTIMATION = 'estimation',
  ORDERING_ONLY = 'ordering_only',
  OUT_OF_THE_BOX = 'out_of_the_box_configuration',
}

const getIsEstimationEnabled = (activeCommerceProduct?: String) =>
  activeCommerceProduct === ActiveCommerceProduct.ESTIMATION;

export const hasFeatureAccess = (
  featureAccesses: HyperionFeatureAccess['featureAccesses'] | undefined,
  featureAccessIdentifier: FeatureAccessIdentifier,
) => {
  if (!featureAccesses) return false;
  return featureAccesses.some(
    ({ identifier }) => identifier === featureAccessIdentifier,
  );
};

const getIsMaterialListEnabled = (activeCommerceProduct?: string) =>
  activeCommerceProduct === ActiveCommerceProduct.OUT_OF_THE_BOX;

const getIsDirectOrderingEnabled = (activeCommerceProduct?: string) =>
  activeCommerceProduct === ActiveCommerceProduct.ORDERING_ONLY;

export const useFeatureAccesses = () => {
  const {
    data: profileData,
    error: profileError,
    loading: profileLoading,
  } = useProfileQuery();

  const { logError } = usePropertyDetailsError();
  useEffect(() => {
    if (profileError) {
      logError(profileError);
    }
  }, [logError, profileError]);

  const { featureAccesses }: HyperionFeatureAccess = useOrg();

  const activeCommerceProduct =
    profileData?.profile?.user.activeCommerceProduct || undefined;

  const isEstimationEnabled = getIsEstimationEnabled(activeCommerceProduct);

  const isMaterialListEnabled = getIsMaterialListEnabled(activeCommerceProduct);

  const loading = profileLoading;
  const isDirectOrderingEnabled = getIsDirectOrderingEnabled(
    activeCommerceProduct,
  );

  const isLeadHomeownerCaptureEnabled = hasFeatureAccess(
    featureAccesses,
    FeatureAccessIdentifier.LeadHomeownerCapture,
  );
  const isProspectEnabled = hasFeatureAccess(
    featureAccesses,
    FeatureAccessIdentifier.Prospect,
  );
  const isBlueprintsEnabled = hasFeatureAccess(
    featureAccesses,
    FeatureAccessIdentifier.Blueprints,
  );
  const isShowDesignStudioEnabled = hasFeatureAccess(
    featureAccesses,
    FeatureAccessIdentifier.ShowDesignStudio,
  );
  const isInspectionChecklistEnabled = hasFeatureAccess(
    featureAccesses,
    FeatureAccessIdentifier.InspectionChecklist,
  );
  const isRenderingsEnabled = hasFeatureAccess(
    featureAccesses,
    FeatureAccessIdentifier.HiDefRendering,
  );

  return {
    isRenderingsEnabled,
    isDirectOrderingEnabled,
    isEstimationEnabled,
    isMaterialListEnabled,
    loading,
    isLeadHomeownerCaptureEnabled,
    isProspectEnabled,
    isBlueprintsEnabled,
    isShowDesignStudioEnabled,
    isInspectionChecklistEnabled,
  };
};
